import type { PartnerFeatureKey, PartnerFeaturesMap } from '@/constants/roles';

/** Server-derived capability flags from the Portal Account Scope session. */
export type PortalAccessMode = 'full' | 'api' | 'starter' | 'affiliate' | 'admin';

export type PortalCapabilities = {
  portalAccessMode: PortalAccessMode;
  canUseStandaloneAdminTools: boolean;
  canBrowseUnscopedLibrary: boolean;
  canManagePartners: boolean;
  canUseAdminGenerationControls: boolean;
  canViewEmbedPreferences: boolean;
  canManageOwnSubscription: boolean;
  /** Read-only access to customer-visible billing, not an admin feature visibility grant. */
  canPreviewPartnerBilling: boolean;
  canCopyWebsiteEmbed: boolean;
  canCopyDynamicEmbed: boolean;
  canCopyImageEmail: boolean;
  /** Paid Substack post inbox (Individual/Business/Enterprise; same gate as image email). */
  canUseSubstackPaidPosts: boolean;
  canManageContentAccess: boolean;
  canManageApiKeys: boolean;
  isDeveloperStarterOnly: boolean;
  /** Partner org has an affiliate record (active/paused/terminated/pending). */
  hasAffiliate: boolean;
  /** Partner is an affiliate-only shell (`subscription_tier === 'affiliate'`). */
  isAffiliateShell: boolean;
};

export const EMPTY_PORTAL_CAPABILITIES: PortalCapabilities = {
  portalAccessMode: 'full',
  canUseStandaloneAdminTools: false,
  canBrowseUnscopedLibrary: false,
  canManagePartners: false,
  canUseAdminGenerationControls: false,
  canViewEmbedPreferences: false,
  canManageOwnSubscription: false,
  canPreviewPartnerBilling: false,
  canCopyWebsiteEmbed: false,
  canCopyDynamicEmbed: false,
  canCopyImageEmail: false,
  canUseSubstackPaidPosts: false,
  canManageContentAccess: false,
  canManageApiKeys: false,
  isDeveloperStarterOnly: false,
  hasAffiliate: false,
  isAffiliateShell: false,
};

export function parsePortalCapabilities(value: unknown): PortalCapabilities {
  if (!value || typeof value !== 'object') {
    return EMPTY_PORTAL_CAPABILITIES;
  }

  const record = value as Record<string, unknown>;
  const portalAccessMode =
    record.portalAccessMode === 'api' ||
    record.portalAccessMode === 'starter' ||
    record.portalAccessMode === 'affiliate' ||
    record.portalAccessMode === 'admin'
      ? record.portalAccessMode
      : 'full';
  return {
    portalAccessMode,
    canUseStandaloneAdminTools: record.canUseStandaloneAdminTools === true,
    canBrowseUnscopedLibrary: record.canBrowseUnscopedLibrary === true,
    canManagePartners: record.canManagePartners === true,
    canUseAdminGenerationControls: record.canUseAdminGenerationControls === true,
    canViewEmbedPreferences: record.canViewEmbedPreferences === true,
    canManageOwnSubscription: record.canManageOwnSubscription === true,
    canPreviewPartnerBilling: record.canPreviewPartnerBilling === true,
    canCopyWebsiteEmbed: record.canCopyWebsiteEmbed === true,
    canCopyDynamicEmbed: record.canCopyDynamicEmbed === true,
    canCopyImageEmail: record.canCopyImageEmail === true,
    canUseSubstackPaidPosts: record.canUseSubstackPaidPosts === true,
    canManageContentAccess: record.canManageContentAccess === true,
    canManageApiKeys: record.canManageApiKeys === true,
    isDeveloperStarterOnly: record.isDeveloperStarterOnly === true,
    hasAffiliate: record.hasAffiliate === true,
    isAffiliateShell: record.isAffiliateShell === true,
  };
}

export function hasPortalFeature(
  features: PartnerFeaturesMap | undefined,
  key: PartnerFeatureKey
): boolean {
  return Boolean(features?.[key]);
}
